import { Controller } from "@hotwired/stimulus"
import * as serverTime from "../lib/server_time"

export default class extends Controller {
  static targets = ["timer", "videoBtn"]
  static values = { userId: Number, serverNow: Number, openingLabel: String }

  initialize() {
    this._reloadPending = false
    this._activeTimers = new WeakSet()
  }

  connect() {
    serverTime.seed(this.serverNowValue)
    serverTime.subscribe(this.userIdValue)

    // Re-sync countdown immediately when user returns to this tab/window.
    // _tick() will detect any deadline that passed while backgrounded and
    // trigger the reload via _updateTimer → _triggerReload.
    this._onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        serverTime.requestSync()
        this._tick()
      }
    }
    document.addEventListener("visibilitychange", this._onVisibilityChange)
  }

  disconnect() {
    this._stopTimers()
    if (this._onVisibilityChange) document.removeEventListener("visibilitychange", this._onVisibilityChange)
    serverTime.unsubscribe()
  }

  // Stimulus calls this automatically when a timer target appears in the DOM
  timerTargetConnected() {
    if (!this.interval) this._startTimers()
    // Immediately update the new timer
    this._tick()
  }

  // Stimulus calls this when a timer target is removed
  timerTargetDisconnected() {
    if (this.timerTargets.length === 0) this._stopTimers()
  }

  _startTimers() {
    this.interval = setInterval(() => this._tick(), 1000)
  }

  _stopTimers() {
    if (this.interval) { clearInterval(this.interval); this.interval = null }
  }

  _tick() {
    if (this.timerTargets.length === 0) return

    const now = serverTime.now()
    if (!now) return // Server time not yet available — keep server-rendered text
    this.timerTargets.forEach((el) => this._updateTimer(el, now))
    this._updateVideoBtn(now)
  }

  _updateVideoBtn(now) {
    if (!this.hasVideoBtnTarget) return

    const btn = this.videoBtnTarget
    // Find the first timer with a deadline to get the distance
    const timerEl = this.timerTargets.find(el => el.dataset.deadlineUtc)
    if (!timerEl) return

    const deadline = new Date(timerEl.dataset.deadlineUtc)
    const diff = deadline.getTime() - now.getTime()

    // Show within ~15 min before deadline (890000ms), hide 1 hour after (-3600000ms)
    const showWithin = parseInt(btn.dataset.showWithin) || 890000
    const hideAfter = parseInt(btn.dataset.hideAfter) || 3600000

    if (diff < showWithin && diff > -hideAfter) {
      btn.classList.remove("hidden")
      btn.classList.add("inline-flex")
    } else {
      btn.classList.add("hidden")
      btn.classList.remove("inline-flex")
    }
  }

  _updateTimer(el, now) {
    if (el.dataset.tbd === "true") {
      el.textContent = el.dataset.tbdLabel || "TBD"
      return
    }

    const deadlineStr = el.dataset.deadlineUtc
    if (!deadlineStr) {
      el.textContent = ""
      return
    }

    const deadline = new Date(deadlineStr)
    const diff = deadline.getTime() - now.getTime()

    if (diff <= 0) {
      el.textContent = el.dataset.endedLabel || ""
      el.classList.remove("text-[#9f48e0]")
      el.classList.add("text-gray-400")
      // The _activeTimers guard prevents false reloads on list pages where
      // other projects already had past deadlines when the page loaded.
      if (this._activeTimers.has(el)) {
        this._triggerReload()
      }
      return
    }

    this._activeTimers.add(el)
    const totalSeconds = Math.floor(diff / 1000)
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const pad = (n) => String(n).padStart(2, "0")

    if (days > 0) {
      const dayLabel = el.dataset.format === "long" ? ` ${el.dataset.daysLabel || "days"} ` : "d "
      el.textContent = `${days}${dayLabel}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    } else {
      el.textContent = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    }
  }

  _triggerReload() {
    if (this._reloadPending) return
    this._reloadPending = true
    this._stopTimers()
    this._showOpeningToast()
    setTimeout(() => window.location.reload(), 4000)
  }

  _showOpeningToast() {
    const toast = document.createElement("div")
    toast.id = "bid-opening-toast"
    toast.className = "flash-toast-info"
    toast.style.opacity = "0"
    toast.innerHTML = `
      <div class="shrink-0">
        <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
      </div>
      <p class="flex-1 text-sm"></p>
    `
    toast.querySelector("p").textContent = this.openingLabelValue || "Opening bids\u2026"
    document.body.appendChild(toast)
    requestAnimationFrame(() => {
      const logo = document.querySelector("[data-company-logo]")
      if (logo) {
        const logoRect = logo.getBoundingClientRect()
        const toastHeight = toast.offsetHeight || 32
        const top = (50 - toastHeight) / 2
        toast.style.top = `${Math.max(top, 4)}px`
        toast.style.right = "auto"
        toast.style.left = `${logoRect.left - toast.offsetWidth - 12}px`
      }
      toast.style.opacity = "1"
    })
  }
}
